import { useEffect, useState } from 'react'
import { Alert, Button, Drawer, Empty, Image, Space, Spin, Tag, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { jobsApi } from '@/api/jobs'
import type { SheetPreviewItem, UUID } from '@/types/api'

const { Text } = Typography

interface Props {
  jobId: UUID
  sheetNos: string[]
  open: boolean
  onClose: () => void
}

function isHttpUrl(url: string | null | undefined): boolean {
  return !!url && /^https?:\/\//i.test(url)
}

function SheetPngImage({ jobId, item }: { jobId: UUID; item: SheetPreviewItem }) {
  const [src, setSrc] = useState<string | null>(isHttpUrl(item.url) ? item.url! : null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (item.status !== 'ready') return
    if (isHttpUrl(item.url)) {
      setSrc(item.url!)
      return
    }
    const path =
      item.url && !item.url.startsWith('/')
        ? item.url
        : `jobs/${jobId}/sheet-png-file?sheet_no=${encodeURIComponent(item.sheet_no)}&sha256=${encodeURIComponent(item.sha256 || '')}`
    let blobUrl: string | null = null
    let cancelled = false
    api
      .get(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return
        blobUrl = URL.createObjectURL(res.data)
        setSrc(blobUrl)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || '图片加载失败')
      })
    return () => {
      cancelled = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [jobId, item.sheet_no, item.sha256, item.status, item.url])

  if (error) {
    return <Text type="danger">{error}</Text>
  }
  if (!src) {
    return <Spin size="small" />
  }
  return (
    <Image
      src={src}
      alt={`图号 ${item.sheet_no}`}
      style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain' }}
    />
  )
}

function itemLabel(item: SheetPreviewItem): string {
  if (item.status === 'pending') return '生成中'
  if (item.status === 'failed') return '失败'
  if (item.status === 'missing') return '图上未检出'
  if (item.status === 'ready') return '已生成'
  return item.status
}

export function SheetPreviewDrawer({ jobId, sheetNos, open, onClose }: Props) {
  const qc = useQueryClient()
  const nos = sheetNos.filter(Boolean)
  const { data, isFetching } = useQuery({
    queryKey: ['sheet-previews', jobId, nos.join(',')],
    queryFn: () => jobsApi.sheetPreviews(jobId, nos),
    enabled: open && nos.length > 0,
    refetchInterval: (q) => {
      const st = q.state.data?.status
      if (st === 'pending' || st === 'partial') return 3000
      const items = q.state.data?.items || []
      if (items.some((it) => it.status === 'pending')) return 3000
      return false
    },
  })

  const retryMutation = useMutation({
    mutationFn: () => jobsApi.retrySheetPng(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet-previews', jobId] })
      qc.invalidateQueries({ queryKey: ['sheet-png-status', jobId] })
    },
  })

  const items: SheetPreviewItem[] =
    data?.items?.length
      ? data.items
      : nos.map((sheet_no) => ({ sheet_no, status: 'pending' }))
  const failed = items.some((it) => it.status === 'failed')
  const pending = items.some((it) => it.status === 'pending') || data?.status === 'pending'
  const showRetry = failed || data?.status === 'failed' || (pending && (data?.total || 0) === 0)

  return (
    <Drawer
      title={`图号预览${nos.length ? `：${nos.join('、')}` : ''}`}
      open={open}
      onClose={onClose}
      width={640}
      extra={
        showRetry ? (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={retryMutation.isPending}
            onClick={() => retryMutation.mutate()}
          >
            {failed || data?.status === 'failed' ? '重试失败图号' : '生成预览'}
          </Button>
        ) : null
      }
    >
      {data?.warnings?.length ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={data.warnings.join('；')}
        />
      ) : null}
      {pending && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={data?.message || '按图号出图进行中'}
        />
      )}
      {items.length === 0 ? (
        <Empty description="没有关联图号" />
      ) : (
        <Image.PreviewGroup>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {items.map((item, idx) => (
              <div key={`${item.sheet_no}-${item.sha256 || idx}`}>
                <Space style={{ marginBottom: 8 }}>
                  <Tag color="blue">{item.sheet_no}</Tag>
                  <Text type="secondary">{itemLabel(item)}</Text>
                  {isFetching && item.status === 'pending' ? <Spin size="small" /> : null}
                </Space>
                {item.status === 'ready' ? (
                  <SheetPngImage jobId={jobId} item={item} />
                ) : item.status === 'failed' ? (
                  <Text type="danger">{item.error_message || '渲染失败'}</Text>
                ) : item.status === 'missing' ? (
                  <Text type="secondary">图纸上没有检出该图号窗口</Text>
                ) : (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin />
                  </div>
                )}
              </div>
            ))}
          </Space>
        </Image.PreviewGroup>
      )}
    </Drawer>
  )
}
